/**
 * The presentation of the Golden Amphora (docs/TIBBS-VOICE.md 4): played only
 * when someone asks for it from the game-over screen. The script is written
 * live (like Single Didge's Medal Ceremony); each podium step rises, with its
 * names, when Tibbs reaches the sentence that presents that prize, so the
 * reveal keeps time with the voice. × skips to the finished podium.
 */
import { useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import type { GameState } from '../../../engine';
import { useAssets } from '../../assets';
import { fmt } from '../../brand';
import type { SeatInfo } from '../../game/types';
import { label, listNames, totalOf } from '../../game/viewModel';
import { reducedMotion } from '../../lib/motion';
import type { Podium, Reveal } from '../../tibbs/ceremony';
import type { TibbsStatus } from '../../tibbs/useTibbs';
import { LAST_PRIZE } from '../../../readout/prizes';
import { Crest, Pic, PlayerMedallion, Tibbs } from '../Art';
import type { TibbsPose } from '../Art';
import { AmphoraIcon, IconClose, IconHome, IconScores } from '../Icons';
import { TibbsCaption } from './TibbsButton';

/** The Pompeii Prize: a lump of lava rock, still warm, drawn in code. */
export function LavaRock({ size = 40 }: { size?: number }) {
  return (
    <svg className="lava-rock" viewBox="0 0 64 48" width={size} height={(size * 48) / 64} aria-hidden="true">
      <ellipse cx="32" cy="43" rx="23" ry="3.5" fill="rgba(60,40,20,0.2)" />
      <path d="M27 15 q-4 -4 0 -7.5 q4 -3.5 0 -7" fill="none" stroke="rgba(120,108,98,0.6)" strokeWidth="2" strokeLinecap="round" />
      <path d="M38 16 q3.5 -3 0 -6 q-3.5 -3 0.5 -6" fill="none" stroke="rgba(120,108,98,0.45)" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M9 41 q-3 -9 5 -15 q4 -9 14 -8.5 q7 -5 14 0.5 q9 1 11.5 10 q5.5 6 1.5 13z" fill="#4d3d36" stroke="#2b211d" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M17 25 q5 -5 11 -5.5 M43 20 q5 1 7 5" fill="none" stroke="#76625a" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M20 41 l4 -7 l-2 -5 l5 -5 M25 34 l5 -1 M41 41 l-3 -6 l4 -4 l-1 -7 M38 35 l-4 -3" fill="none" stroke="#e8772a" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M20 41 l4 -7 l-2 -5 l5 -5 M25 34 l5 -1 M41 41 l-3 -6 l4 -4 l-1 -7 M38 35 l-4 -3" fill="none" stroke="#ffd166" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Laurels() {
  const bits = useMemo(
    () =>
      Array.from({ length: 28 }, (_, i) => ({
        left: Math.random() * 100,
        delay: Math.random() * 1,
        dur: 1.8 + Math.random() * 1.2,
        size: 10 + Math.random() * 8,
        rot: Math.random() * 360,
        gold: i % 3 === 0,
      })),
    [],
  );
  return (
    <div className="laurels" aria-hidden="true">
      {bits.map((b, i) => (
        <span
          key={i}
          className={`leaf${b.gold ? ' gold' : ''}`}
          style={{ left: `${b.left}%`, animationDelay: `${b.delay}s`, animationDuration: `${b.dur}s`, width: b.size, height: b.size * 0.5, '--r': `${b.rot}deg` } as CSSProperties}
        />
      ))}
    </div>
  );
}

export function Ceremony({
  state,
  seats,
  me,
  mode,
  isHost,
  podium,
  reveal,
  loadingText,
  caption,
  pose,
  status,
  onSkip,
  onClose,
  onPlayAgain,
  onHome,
  onScores,
}: {
  state: GameState;
  seats: SeatInfo[];
  me: number | null;
  mode: 'solo' | 'online';
  isHost: boolean;
  podium: Podium;
  reveal: Reveal;
  loadingText?: string;
  caption: string | null;
  pose: TibbsPose | null;
  status: TibbsStatus;
  onSkip: () => void;
  onClose: () => void;
  onPlayAgain: () => void;
  onHome: () => void;
  onScores: () => void;
}) {
  const a = useAssets();
  const finished = status === 'done' || status === 'idle' || status === 'failed';
  const twoPlayer = state.players.length === 2;
  const goldUp = finished || reveal.gold;
  const lastUp = finished || reveal.last;
  const shownTier = (seat: number) =>
    finished ||
    (podium.gold.includes(seat) && reveal.gold) ||
    (podium.silver.includes(seat) && reveal.silver) ||
    (podium.bronze.includes(seat) && reveal.bronze) ||
    (podium.last.includes(seat) && reveal.last);
  const shown = (seat: number) => shownTier(seat);
  const name = (seat: number) => label(seats, me, seat);
  const names = (list: number[]) => listNames(list.filter(shown).map(name));
  const total = (seat: number) => fmt(totalOf(state.players[seat]));
  const lastSeats = podium.last;
  const iWon = me !== null && podium.gold.includes(me);
  const [laurels, setLaurels] = useState(false);

  useEffect(() => {
    if (!goldUp || reducedMotion()) return;
    setLaurels(true);
    const t = setTimeout(() => setLaurels(false), 3200);
    return () => clearTimeout(t);
  }, [goldUp]);

  const step = (kind: 'gold' | 'silver' | 'bronze', list: number[], up: boolean) => (
    <div className={`cer-step ${kind}${up ? ' up' : ''}`}>
      <div className="cer-who">
        {up &&
          list.map((s) => (
            <span key={s} className={`cer-name${shown(s) ? ' on' : ''}`}>
              <PlayerMedallion seat={s} size={26} />
              <span className="cer-name-text">{shown(s) ? name(s) : ' '}</span>
            </span>
          ))}
      </div>
      {kind === 'gold' && up && (
        <div className="cer-trophy">
          <Pic img={iWon ? a.trophyWin : (a.trophyOther ?? a.trophyWin)} eager fallback={<AmphoraIcon size={56} />} />
        </div>
      )}
      <div className="cer-block">
        <span className="cer-place num">{kind === 'gold' ? 1 : kind === 'silver' ? 2 : 3}</span>
        {up && list.length > 0 && <span className="cer-total num">{list.map(total).join(' · ')}</span>}
      </div>
    </div>
  );

  const world = a.worlds.get(9);
  return (
    <div className="ceremony" role="dialog" aria-modal="true" aria-labelledby="cer-title">
      {world?.travel && (
        <div className="cer-bg" aria-hidden="true">
          <Pic img={world.travelPhone ?? world.travel} eager />
        </div>
      )}
      {laurels && <Laurels />}
      <div className="cer-panel panel">
        <button type="button" className="icon-btn cer-x" onClick={finished ? onClose : onSkip} aria-label={finished ? 'Close the presentation' : 'Skip to the podium'}>
          <IconClose />
        </button>
        <p className="cer-kicker mark">The Old World Tour</p>
        <h2 id="cer-title" className="cer-title">
          Presentation of the Golden Amphora
        </h2>
        <div className={`cer-crest${reveal.crest || finished ? ' on' : ''}`}>
          <Crest width={84} />
        </div>

        {/* A step only for a prize someone won: a tie for last can leave no Silver Laurel. */}
        <div className={`cer-podium${twoPlayer || !podium.silver.length ? ' two' : ''}`}>
          {!twoPlayer && podium.silver.length > 0 && step('silver', podium.silver, finished || reveal.silver)}
          {step('gold', podium.gold, goldUp)}
          {!twoPlayer && podium.bronze.length > 0 && step('bronze', podium.bronze, finished || reveal.bronze)}
        </div>

        {lastSeats.length > 0 && (
          <div className={`cer-last${lastUp ? ' on' : ''}`}>
            <LavaRock size={44} />
            <span className="cer-last-label">
              <span className="cer-last-title">{LAST_PRIZE.name}</span>
              <span className="cer-last-name">{lastUp ? names(lastSeats) || ' ' : ' '}</span>
            </span>
            {lastUp && <span className="cer-total num">{lastSeats.map(total).join(' · ')}</span>}
          </div>
        )}

        <div className="cer-voice">
          {!finished && <Tibbs key={pose ?? 'wry'} pose={pose ?? 'wry'} size={34} className="cer-tibbs" />}
          <TibbsCaption text={finished ? null : (caption ?? (status === 'loading' ? (loadingText ?? null) : null))} />
        </div>

        {finished && (
          <div className="cer-actions">
            {mode === 'solo' || isHost ? (
              <button className="btn btn-gold btn-big" onClick={onPlayAgain} data-autofocus>
                Play again
              </button>
            ) : (
              <span className="ready-wait">
                <span className="spinner small" aria-hidden="true" /> Waiting for the host…
              </span>
            )}
            <div className="go-secondary">
              <button className="btn btn-ink btn-sm" onClick={onClose}>
                Standings
              </button>
              <button className="btn btn-ink btn-sm" onClick={onHome}>
                <IconHome /> Home
              </button>
              <button className="icon-btn go-sc" onClick={onScores} aria-label="Final scorecard">
                <IconScores />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
